import React, { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const CATEGORIES = [
  { value: '', label: '選択してください' },
  { value: 'service', label: 'サービス・機能について' },
  { value: 'bug', label: '不具合の報告' },
  { value: 'business', label: '業務・提携のお問い合わせ' },
  { value: 'other', label: 'その他' },
];

const FORM_NAME = 'contact';

export default function Contact() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [botField, setBotField] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      setStatus('error');
      return;
    }
    setStatus('sending');

    const formData = new URLSearchParams();
    formData.append('form-name', FORM_NAME);
    formData.append('name', name.trim());
    formData.append('email', email.trim());
    formData.append('category', category);
    formData.append('message', message.trim());
    if (botField) formData.append('bot-field', botField);

    try {
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setName('');
        setEmail('');
        setCategory('');
        setMessage('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">お問い合わせ</h1>
          <p className="text-gray-600 text-sm mb-10">
            サービスに関するご質問・ご要望は以下のフォームからお送りください。内容を確認のうえ、ご連絡いたします。
          </p>

          {status === 'success' && (
            <div className="mb-8 p-4 rounded-lg bg-green-50 border border-green-200 text-green-800">
              <p className="font-medium">送信が完了しました。</p>
              <p className="text-sm mt-1">お問い合わせいただきありがとうございます。内容を確認のうえ、ご登録のメールアドレス宛にご返信いたします。</p>
            </div>
          )}

          {status === 'error' && (
            <div className="mb-8 p-4 rounded-lg bg-red-50 border border-red-200 text-red-800">
              <p className="font-medium">送信に失敗しました。</p>
              <p className="text-sm mt-1">
                しばらく経ってから再度お試しいただくか、<a href="mailto:support@socialtrip.jp" className="underline">support@socialtrip.jp</a> まで直接メールにてお問い合わせください。
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* スパム対策: 画面上非表示（botが記入した場合は送信しない想定） */}
            <div className="absolute -left-[9999px]" aria-hidden="true">
              <label>
                記入しないでください: <input tabIndex={-1} name="bot-field" value={botField} onChange={(e) => setBotField(e.target.value)} />
              </label>
            </div>
            <input type="hidden" name="form-name" value={FORM_NAME} />

            <div>
              <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-1">
                お名前 <span className="text-red-500">*</span>
              </label>
              <input
                id="contact-name"
                type="text"
                name="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#02D0F0] focus:border-[#02D0F0] outline-none"
                placeholder="山田 太郎"
              />
            </div>

            <div>
              <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-1">
                メールアドレス <span className="text-red-500">*</span>
              </label>
              <input
                id="contact-email"
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#02D0F0] focus:border-[#02D0F0] outline-none"
                placeholder="example@email.com"
              />
            </div>

            <div>
              <label htmlFor="contact-category" className="block text-sm font-medium text-gray-700 mb-1">
                お問い合わせ種別
              </label>
              <select
                id="contact-category"
                name="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#02D0F0] focus:border-[#02D0F0] outline-none bg-white"
              >
                {CATEGORIES.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-1">
                お問い合わせ内容 <span className="text-red-500">*</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#02D0F0] focus:border-[#02D0F0] outline-none resize-y"
                placeholder="ご質問やご要望をご記入ください"
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full sm:w-auto px-8 py-3 bg-[#02D0F0] hover:bg-[#02b8d9] text-white font-medium rounded-xl shadow-md hover:shadow-lg transition disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {status === 'sending' ? '送信中...' : '送信する'}
              </button>
            </div>
          </form>

          <p className="mt-8 text-sm text-gray-500">
            メールでのお問い合わせ: <a href="mailto:support@socialtrip.jp" className="text-[#02D0F0] hover:underline">support@socialtrip.jp</a>
          </p>
        </div>
      </main>
      <Footer />
    </>
  );
}
